<template>
  <section class="security-page" :dir="direction">
    <header>
      <button type="button" :aria-label="$t('securityUi.back')" @click="$emit('close')"><i class="el-icon-arrow-left" /></button>
      <div><small>{{ $t("securityUi.eyebrow") }}</small><h1>{{ $t("securityUi.title") }}</h1></div>
      <span />
    </header>
    <main class="security-scroll">
      <div v-if="loading" class="security-loading"><i class="el-icon-loading" /><span>{{ $t("securityUi.loading") }}</span></div>
      <template v-else>
        <section class="email-identity">
          <span><i class="el-icon-message" /></span>
          <div><small>{{ $t("securityUi.currentEmail") }}</small><strong>{{ overview.email_masked || $t("securityUi.emailUnbound") }}</strong><p>{{ $t(overview.email_bound ? "securityUi.verifiedEmail" : "securityUi.bindEmailTip") }}</p></div>
          <em v-if="overview.email_bound"><i class="el-icon-lock" /> {{ $t("securityUi.protected") }}</em>
        </section>

        <section class="security-block">
          <button type="button" class="block-heading" :aria-expanded="activePanel === 'username'" @click="togglePanel('username')">
            <span class="block-icon"><i class="el-icon-user" /></span>
            <span><strong>{{ $t("securityUi.loginUsername") }}</strong><small>{{ overview.username || $t("securityUi.usernameMissing") }}</small></span>
            <i :class="activePanel === 'username' ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
          </button>
          <form v-if="activePanel === 'username'" class="security-form" @submit.prevent="submitUsername">
            <label><span>{{ $t("securityUi.newUsername") }}</span><input v-model.trim="usernameForm.username" maxlength="64" autocomplete="username" :placeholder="$t('securityUi.usernamePlaceholder')" /></label>
            <p>{{ $t("securityUi.usernameTip") }}</p>
            <button class="primary-action" type="submit" :disabled="usernameSaving">{{ $t(usernameSaving ? "securityUi.savingUsername" : "securityUi.submitUsername") }}</button>
          </form>
        </section>

        <section class="security-block">
          <button type="button" class="block-heading" :aria-expanded="activePanel === 'email'" @click="togglePanel('email')">
            <span class="block-icon"><i class="el-icon-refresh" /></span>
            <span><strong>{{ $t(overview.email_bound ? "securityUi.changeEmail" : "securityUi.bindEmail") }}</strong><small>{{ $t(overview.email_bound ? "securityUi.changeEmailTip" : "securityUi.bindEmailTip") }}</small></span>
            <i :class="activePanel === 'email' ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
          </button>
          <form v-if="activePanel === 'email'" class="security-form" @submit.prevent="submitEmail">
            <label><span>{{ $t("securityUi.newEmail") }}</span><input v-model.trim="emailForm.new_email" type="email" maxlength="100" autocomplete="email" :placeholder="$t('securityUi.newEmailPlaceholder')" /></label>
            <label v-if="overview.email_bound">
              <span>{{ $t("securityUi.currentCode") }}</span>
              <div class="code-input"><input v-model.trim="emailForm.current_code" inputmode="numeric" maxlength="6" autocomplete="one-time-code" :placeholder="$t('securityUi.codePlaceholder')" /><button type="button" :disabled="codeLoading.current_email || countdowns.current_email > 0" @click="sendCode('current_email')">{{ codeButtonText("current_email") }}</button></div>
            </label>
            <label>
              <span>{{ $t("securityUi.newCode") }}</span>
              <div class="code-input"><input v-model.trim="emailForm.new_code" inputmode="numeric" maxlength="6" autocomplete="one-time-code" :placeholder="$t('securityUi.codePlaceholder')" /><button type="button" :disabled="codeLoading.new_email || countdowns.new_email > 0" @click="sendCode('new_email')">{{ codeButtonText("new_email") }}</button></div>
            </label>
            <button class="primary-action" type="submit" :disabled="emailSaving">{{ $t(emailSaving ? "securityUi.changingEmail" : "securityUi.submitEmail") }}</button>
          </form>
        </section>

        <section class="security-block">
          <button type="button" class="block-heading" :disabled="!overview.email_bound" :aria-expanded="activePanel === 'password'" @click="togglePanel('password')">
            <span class="block-icon password"><i class="el-icon-key" /></span>
            <span><strong>{{ $t("securityUi.changePassword") }}</strong><small>{{ $t(overview.email_bound ? "securityUi.changePasswordTip" : "securityUi.bindBeforeSensitive") }}</small></span>
            <i :class="activePanel === 'password' ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
          </button>
          <form v-if="activePanel === 'password'" class="security-form" @submit.prevent="submitPassword">
            <label>
              <span>{{ $t("securityUi.passwordCode") }}</span>
              <div class="code-input"><input v-model.trim="passwordForm.code" inputmode="numeric" maxlength="6" autocomplete="one-time-code" :placeholder="$t('securityUi.codePlaceholder')" /><button type="button" :disabled="codeLoading.password || countdowns.password > 0" @click="sendCode('password')">{{ codeButtonText("password") }}</button></div>
            </label>
            <label><span>{{ $t("securityUi.newPassword") }}</span><input v-model="passwordForm.password" type="password" maxlength="72" autocomplete="new-password" :placeholder="$t('securityUi.passwordPlaceholder')" /></label>
            <label><span>{{ $t("securityUi.confirmPassword") }}</span><input v-model="passwordForm.password_confirmation" type="password" maxlength="72" autocomplete="new-password" :placeholder="$t('securityUi.confirmPasswordPlaceholder')" /></label>
            <button class="primary-action" type="submit" :disabled="passwordSaving">{{ $t(passwordSaving ? "securityUi.changingPassword" : "securityUi.submitPassword") }}</button>
          </form>
        </section>

        <section class="security-activity">
          <div class="activity-heading">
            <div><small>{{ $t("securityUi.deviceEyebrow") }}</small><h2>{{ $t("securityUi.loginDevices") }}</h2><p>{{ $t("securityUi.loginDevicesTip") }}</p></div>
            <span>{{ $n(devices.length) }}</span>
          </div>
          <div v-if="devices.length" class="device-list">
            <article v-for="device in devices" :key="`${device.session_type}-${device.id}`" class="device-card" :class="{ current: device.current }">
              <span class="device-icon"><i :class="deviceIcon(device)" /></span>
              <div class="device-copy">
                <div><strong>{{ deviceTitle(device) }}</strong><em v-if="device.current">{{ $t("securityUi.currentDevice") }}</em><em v-else-if="device.session_type === 'companion'" class="companion">{{ $t("securityUi.companionDevice") }}</em></div>
                <small>{{ deviceSubtitle(device) }}</small>
                <p><span><i class="el-icon-location-outline" />{{ device.ip_masked || $t("securityUi.unknownIp") }}</span><span><i class="el-icon-time" />{{ $t("securityUi.lastActive") }} {{ formatDate(device.last_active_time) }}</span></p>
              </div>
              <button v-if="!device.current" type="button" :disabled="revokingKey === `${device.session_type}-${device.id}`" @click="confirmRevoke(device)">
                {{ $t(revokingKey === `${device.session_type}-${device.id}` ? "securityUi.revoking" : "securityUi.forceLogout") }}
              </button>
            </article>
          </div>
          <div v-else class="activity-empty"><i class="el-icon-monitor" /><span>{{ $t("securityUi.deviceEmpty") }}</span></div>
        </section>

        <section class="security-activity qr-activity">
          <div class="activity-heading">
            <div><small>{{ $t("securityUi.scanEyebrow") }}</small><h2>{{ $t("securityUi.scanRecords") }}</h2><p>{{ $t("securityUi.scanRecordsTip") }}</p></div>
            <span>{{ $n(qrPagination.total || 0) }}</span>
          </div>
          <div v-if="qrRecords.length" class="qr-record-list">
            <article v-for="record in qrRecords" :key="record.id" class="qr-record">
              <span class="qr-icon"><i class="el-icon-full-screen" /></span>
              <div>
                <div><strong>{{ record.client_name }}</strong><em :class="`status-${record.status}`">{{ $t(`securityUi.scanStatus.${record.status}`) }}</em></div>
                <small>{{ record.device_name || $t("securityUi.unknownDevice") }}</small>
                <p>{{ record.request_ip_masked || $t("securityUi.unknownIp") }} · {{ formatDate(record.approved_time || record.update_time || record.create_time) }}</p>
              </div>
            </article>
            <button v-if="qrPagination.has_more" class="load-more" type="button" :disabled="qrLoadingMore" @click="loadMoreQrRecords">
              {{ $t(qrLoadingMore ? "securityUi.loadingMore" : "securityUi.loadMore") }}
            </button>
          </div>
          <div v-else class="activity-empty"><i class="el-icon-full-screen" /><span>{{ $t("securityUi.scanEmpty") }}</span></div>
        </section>
        <p class="session-note"><i class="el-icon-monitor" />{{ $t("securityUi.otherSessions") }}</p>
      </template>
    </main>
  </section>
</template>

<script>
import { localeState } from "../i18n"
import { getQrLoginRecords, getSecurityDevices, getSecurityOverview, revokeSecuritySession, sendSecurityCode, updateSecurityEmail, updateSecurityPassword, updateSecurityUsername } from "../api/security"

export default {
  name: "SecuritySettings",
  data() {
    return {
      loading: true, overview: {}, activePanel: "",
      emailForm: { new_email: "", current_code: "", new_code: "" },
      usernameForm: { username: "" },
      passwordForm: { code: "", password: "", password_confirmation: "" },
      countdowns: { current_email: 0, new_email: 0, password: 0 },
      codeLoading: { current_email: false, new_email: false, password: false },
      timers: {}, emailSaving: false, passwordSaving: false, usernameSaving: false,
      devices: [], qrRecords: [], qrPagination: { page: 1, total: 0, has_more: false },
      revokingKey: "", qrLoadingMore: false
    }
  },
  computed: {
    direction() {
      return String(localeState.language).toLowerCase().startsWith("ar") ? "rtl" : "ltr"
    }
  },
  created() {
    this.loadOverview()
  },
  beforeDestroy() {
    Object.keys(this.timers).forEach(key => clearInterval(this.timers[key]))
  },
  methods: {
    async loadOverview() {
      this.loading = true
      try {
        const [overviewRes, deviceRes, qrRes] = await Promise.all([
          getSecurityOverview(),
          getSecurityDevices(),
          getQrLoginRecords({ page: 1, page_size: 10 })
        ])
        this.overview = overviewRes.data || {}
        this.usernameForm.username = this.overview.username || ""
        this.devices = (deviceRes.data && deviceRes.data.devices) || []
        this.qrRecords = (qrRes.data && qrRes.data.list) || []
        this.qrPagination = (qrRes.data && qrRes.data.pagination) || { page: 1, total: 0, has_more: false }
      } catch (error) {
        this.overview = {}
      } finally {
        this.loading = false
      }
    },
    togglePanel(panel) {
      if (panel === "password" && !this.overview.email_bound) {
        this.$notice.warning(this.$t("securityUi.bindBeforeSensitive"))
        return
      }
      this.activePanel = this.activePanel === panel ? "" : panel
    },
    formatDate(value) {
      return this.$d(value, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    },
    deviceIcon(device) {
      if (device.session_type === "companion") return "el-icon-video-camera"
      if (device.device_kind === "mobile" || device.device_kind === "tablet") return "el-icon-mobile-phone"
      return "el-icon-monitor"
    },
    deviceTitle(device) {
      if (device.session_type === "companion") return device.device_name || device.client_name || this.$t("securityUi.unknownDevice")
      return [device.browser, device.os].filter(Boolean).join(" · ") || this.$t("securityUi.unknownDevice")
    },
    deviceSubtitle(device) {
      if (device.session_type === "companion") {
        const method = this.$t(`securityUi.authMethod.${device.auth_method || "qr"}`)
        return [device.client_name, method].filter(Boolean).join(" · ")
      }
      return this.$t("securityUi.h5Session")
    },
    async confirmRevoke(device) {
      const title = this.deviceTitle(device)
      try {
        await this.$notice.confirm(
          this.$t("securityUi.revokeConfirm", { device: title }),
          this.$t("securityUi.revokeTitle")
        )
      } catch (error) {
        return
      }
      const key = `${device.session_type}-${device.id}`
      this.revokingKey = key
      try {
        await revokeSecuritySession({ session_type: device.session_type, session_id: device.id })
        this.devices = this.devices.filter(item => `${item.session_type}-${item.id}` !== key)
        this.$notice.success(this.$t("securityUi.revokeSuccess"))
      } catch (error) {
        // 请求层已显示标准错误码对应的具体原因。
      } finally {
        this.revokingKey = ""
      }
    },
    async loadMoreQrRecords() {
      if (this.qrLoadingMore || !this.qrPagination.has_more) return
      this.qrLoadingMore = true
      try {
        const nextPage = Number(this.qrPagination.page || 1) + 1
        const res = await getQrLoginRecords({ page: nextPage, page_size: 10 })
        const incoming = (res.data && res.data.list) || []
        const known = new Set(this.qrRecords.map(item => item.id))
        this.qrRecords = this.qrRecords.concat(incoming.filter(item => !known.has(item.id)))
        this.qrPagination = (res.data && res.data.pagination) || this.qrPagination
      } catch (error) {
        // 请求层统一展示错误详情。
      } finally {
        this.qrLoadingMore = false
      }
    },
    isCode(value) {
      return /^\d{6}$/.test(String(value || ""))
    },
    validEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || ""))
    },
    validPassword(value) {
      return String(value || "").length >= 8 && String(value || "").length <= 72 && /[A-Za-z]/.test(value) && /\d/.test(value)
    },
    codeButtonText(scene) {
      return this.countdowns[scene] > 0 ? this.$t("securityUi.resendIn", { seconds: this.countdowns[scene] }) : this.$t("securityUi.sendCode")
    },
    startCountdown(scene, seconds) {
      clearInterval(this.timers[scene])
      this.countdowns[scene] = Number(seconds || 60)
      this.timers[scene] = setInterval(() => {
        this.countdowns[scene] = Math.max(0, this.countdowns[scene] - 1)
        if (this.countdowns[scene] === 0) {
          clearInterval(this.timers[scene])
          delete this.timers[scene]
        }
      }, 1000)
    },
    async sendCode(scene) {
      if (scene === "new_email" && !this.validEmail(this.emailForm.new_email)) {
        this.$notice.warning(this.$t("securityUi.emailRequired"))
        return
      }
      this.codeLoading[scene] = true
      try {
        const res = await sendSecurityCode({ scene, new_email: scene === "new_email" ? this.emailForm.new_email : "" })
        this.startCountdown(scene, res.data.countdown)
        this.$notice.success(this.$t("common.success"))
      } catch (error) {
        // 请求层已使用主题消息窗展示标准错误码对应的具体原因。
      } finally {
        this.codeLoading[scene] = false
      }
    },
    async submitEmail() {
      if (!this.validEmail(this.emailForm.new_email)) {
        this.$notice.warning(this.$t("securityUi.emailRequired"))
        return
      }
      if ((this.overview.email_bound && !this.isCode(this.emailForm.current_code)) || !this.isCode(this.emailForm.new_code)) {
        this.$notice.warning(this.$t("securityUi.codeRequired"))
        return
      }
      this.emailSaving = true
      try {
        const res = await updateSecurityEmail(this.emailForm)
        this.overview.email_masked = res.data.email_masked
        this.overview.email_bound = true
        this.emailForm = { new_email: "", current_code: "", new_code: "" }
        this.activePanel = ""
        this.$emit("email-updated", { email: res.data.email })
        this.$notice.success(this.$t("securityUi.emailChanged"))
      } catch (error) {
        // 请求层已统一提示失败原因，组件只负责恢复提交状态。
      } finally {
        this.emailSaving = false
      }
    },
    async submitUsername() {
      if (!/^[A-Za-z0-9][A-Za-z0-9._-]{2,63}$/.test(this.usernameForm.username)) {
        this.$notice.warning(this.$t("securityUi.usernameInvalid"))
        return
      }
      this.usernameSaving = true
      try {
        const res = await updateSecurityUsername(this.usernameForm)
        this.overview.username = res.data.username
        this.overview.username_is_generated = false
        this.activePanel = ""
        this.$notice.success(this.$t("securityUi.usernameChanged"))
      } catch (error) {
        // 请求层展示标准错误原因。
      } finally {
        this.usernameSaving = false
      }
    },
    async submitPassword() {
      if (!this.isCode(this.passwordForm.code)) {
        this.$notice.warning(this.$t("securityUi.codeRequired"))
        return
      }
      if (!this.validPassword(this.passwordForm.password)) {
        this.$notice.warning(this.$t("securityUi.passwordInvalid"))
        return
      }
      if (this.passwordForm.password !== this.passwordForm.password_confirmation) {
        this.$notice.warning(this.$t("securityUi.passwordMismatch"))
        return
      }
      this.passwordSaving = true
      try {
        await updateSecurityPassword(this.passwordForm)
        this.passwordForm = { code: "", password: "", password_confirmation: "" }
        this.overview.has_password = true
        this.activePanel = ""
        this.$notice.success(this.$t("securityUi.passwordChanged"))
      } catch (error) {
        // 请求层已统一提示失败原因，避免产生未处理的 Promise。
      } finally {
        this.passwordSaving = false
      }
    }
  }
}
</script>

<style scoped>
.security-page{position:fixed;z-index:4010;inset:0;margin:auto;width:min(100vw,430px);display:flex;flex-direction:column;overflow:hidden;color:#f8f5ef;background:radial-gradient(circle at 86% 0,rgba(255,61,87,.23),transparent 31%),radial-gradient(circle at 0 75%,rgba(85,113,255,.11),transparent 32%),#0d0e11}.security-page>header{height:calc(82px + env(safe-area-inset-top));padding:env(safe-area-inset-top) 18px 0;display:grid;grid-template-columns:40px 1fr 40px;align-items:center;border-bottom:1px solid rgba(255,255,255,.07)}header>button{width:36px;height:36px;border:1px solid #363840;border-radius:50%;background:#191a1f;color:#fff}header>div{text-align:center}header small{display:block;color:#ff5368;font-size:8px;letter-spacing:2.4px}header h1{margin:4px 0 0;font-size:23px}.security-scroll{flex:1;overflow-y:auto;padding:18px 16px calc(30px + env(safe-area-inset-bottom));scrollbar-width:none}.security-scroll::-webkit-scrollbar{display:none}.security-loading{min-height:300px;display:grid;place-content:center;justify-items:center;gap:12px;color:#858890;font-size:11px}.security-loading i{color:#ff5368;font-size:26px}.email-identity{display:flex;align-items:center;gap:12px;padding:17px;border:1px solid rgba(255,255,255,.1);border-radius:21px;background:linear-gradient(145deg,rgba(255,255,255,.1),rgba(255,255,255,.025));box-shadow:0 18px 40px rgba(0,0,0,.18)}.email-identity>span{width:46px;height:46px;display:grid;place-items:center;flex:none;border-radius:15px;background:rgba(255,75,99,.13);color:#ff6f82;font-size:19px}.email-identity>div{min-width:0;flex:1}.email-identity small,.email-identity strong,.email-identity p{display:block;margin:0}.email-identity small{color:#858890;font-size:9px}.email-identity strong{margin-top:4px;overflow:hidden;text-overflow:ellipsis;font-size:14px;white-space:nowrap}.email-identity p{margin-top:5px;color:#73767e;font-size:9px;line-height:1.4}.email-identity em{display:flex;align-items:center;gap:4px;flex:none;padding:5px 8px;border-radius:9px;background:rgba(72,205,139,.1);color:#6ddeaa;font-size:8px;font-style:normal}.security-block{margin-top:13px;border:1px solid #292b31;border-radius:19px;background:#17181d;overflow:hidden}.block-heading{width:100%;min-height:76px;display:flex;align-items:center;gap:12px;padding:13px;border:0;background:transparent;color:#fff;text-align:start}.block-heading>span:nth-child(2){min-width:0;flex:1}.block-heading strong,.block-heading small{display:block}.block-heading strong{font-size:13px}.block-heading small{margin-top:5px;color:#85878f;font-size:9px;line-height:1.45}.block-heading>i{color:#62646c}.block-icon{width:41px;height:41px;display:grid;place-items:center;flex:none;border-radius:13px;background:#292a30;color:#ff6579;font-size:17px}.block-icon.password{color:#8ca9ff}.security-form{padding:3px 14px 15px;border-top:1px solid #292b31}.security-form label{display:block;margin-top:13px}.security-form label>span{display:block;margin:0 2px 7px;color:#9a9ca4;font-size:9px}.security-form input{box-sizing:border-box;width:100%;height:44px;padding:0 13px;border:1px solid #33353c;border-radius:12px;outline:none;background:#202126;color:#fff;font-size:12px}.security-form input::placeholder{color:#666871}.security-form input:focus{border-color:#ff5368;box-shadow:0 0 0 3px rgba(255,83,104,.09)}.code-input{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px}.code-input button{min-width:94px;padding:0 10px;border:1px solid rgba(255,83,104,.3);border-radius:12px;background:rgba(255,83,104,.09);color:#ff7184;font-size:10px}.code-input button:disabled{opacity:.45}.primary-action{width:100%;height:45px;margin-top:17px;border:0;border-radius:13px;background:linear-gradient(135deg,#ff3358,#ff6479);box-shadow:0 10px 24px rgba(255,51,88,.2);color:#fff;font-size:12px;font-weight:700}.primary-action:disabled{opacity:.55}.session-note{display:flex;align-items:flex-start;gap:8px;margin:18px 3px 0;color:#777a83;font-size:9px;line-height:1.6}.session-note i{margin-top:2px;color:#8ca9ff}[dir="rtl"] header>button i{transform:rotate(180deg)}[dir="rtl"] .block-heading{text-align:right}@media(max-width:350px){.email-identity em{display:none}.code-input{grid-template-columns:1fr}.code-input button{height:38px}}
.security-activity{margin-top:22px}.activity-heading{display:flex;align-items:flex-start;gap:12px;padding:0 3px 10px}.activity-heading>div{min-width:0;flex:1}.activity-heading small{color:#ff6579;font-size:8px;letter-spacing:1.7px}.activity-heading h2{margin:4px 0 0;font-size:15px}.activity-heading p{margin:5px 0 0;color:#777a83;font-size:9px;line-height:1.45}.activity-heading>span{min-width:25px;padding:5px 7px;border-radius:9px;background:#202127;color:#9da0a9;font-size:9px;text-align:center}.device-list,.qr-record-list{display:grid;gap:8px}.device-card,.qr-record{display:flex;align-items:center;gap:10px;padding:12px;border:1px solid #292b31;border-radius:16px;background:#17181d}.device-card.current{border-color:rgba(73,205,139,.3);background:linear-gradient(135deg,rgba(73,205,139,.075),#17181d 58%)}.device-icon,.qr-icon{width:40px;height:40px;display:grid;place-items:center;flex:none;border-radius:13px;background:#26282e;color:#8ca9ff;font-size:17px}.device-card.current .device-icon{background:rgba(73,205,139,.1);color:#6ddeaa}.device-copy,.qr-record>div{min-width:0;flex:1}.device-copy>div,.qr-record>div>div{display:flex;align-items:center;gap:7px;min-width:0}.device-copy strong,.qr-record strong{min-width:0;overflow:hidden;text-overflow:ellipsis;font-size:11px;white-space:nowrap}.device-copy em,.qr-record em{flex:none;padding:3px 6px;border-radius:7px;background:rgba(73,205,139,.1);color:#6ddeaa;font-size:7px;font-style:normal}.device-copy em.companion{background:rgba(140,169,255,.11);color:#9bb4ff}.device-copy>small,.qr-record>div>small{display:block;margin-top:5px;overflow:hidden;color:#858891;font-size:8px;text-overflow:ellipsis;white-space:nowrap}.device-copy p{display:flex;flex-wrap:wrap;gap:5px 10px;margin:6px 0 0;color:#666973;font-size:7px}.device-copy p span{display:flex;align-items:center;gap:3px}.device-card>button{flex:none;padding:7px 8px;border:1px solid rgba(255,83,104,.25);border-radius:9px;background:rgba(255,83,104,.08);color:#ff7184;font-size:8px}.device-card>button:disabled{opacity:.45}.activity-empty{min-height:88px;display:grid;place-content:center;justify-items:center;gap:8px;border:1px dashed #2d2f36;border-radius:16px;color:#6f727b;font-size:9px}.activity-empty i{font-size:21px}.qr-record em{background:rgba(140,169,255,.1);color:#9bb4ff}.qr-record em.status-denied,.qr-record em.status-expired{background:rgba(255,83,104,.08);color:#ff7184}.qr-record em.status-consumed{background:rgba(73,205,139,.1);color:#6ddeaa}.qr-record p{margin:5px 0 0;color:#666973;font-size:8px}.load-more{width:100%;height:38px;border:1px solid #30323a;border-radius:12px;background:#1b1c21;color:#9da0a9;font-size:9px}.load-more:disabled{opacity:.5}[dir="rtl"] .activity-heading,[dir="rtl"] .device-card,[dir="rtl"] .qr-record{text-align:right}@media(max-width:350px){.device-card{align-items:flex-start;flex-wrap:wrap}.device-copy{min-width:calc(100% - 52px)}.device-card>button{margin-inline-start:50px}}
</style>
